// src/utils/analytics.js
// Lightweight analytics helper. Fires events through TWO parallel sinks:
//   - Google Analytics 4 (gtag.js) — legacy aggregate funnel
//   - PostHog — product analytics, funnels, session replay, feature flags
// Each sink no-ops if its env var isn't set, so the same code works in
// dev (logs to console only) and prod (whichever sink is configured).

import posthog from 'posthog-js';

const ATTRIBUTION_KEY = 'eventory.attribution';
const CONSENT_KEY = 'eventory.analyticsConsent';
const GA_SCRIPT_ATTR = 'data-eventory-ga-loader';
const GA4_SCRIPT_BASE = 'https://www.googletagmanager.com/gtag/js?id=';
let gaBootstrapped = false;
let posthogBootstrapped = false;

const ATTR_KEYS = [
	'utm_source',
	'utm_medium',
	'utm_campaign',
	'utm_term',
	'utm_content',
	'gclid',
	'fbclid',
];

const canUseBrowser = () => typeof window !== 'undefined';
const toBool = (value) => String(value || '').toLowerCase() === 'true';
const getMeasurementId = () =>
	String(process.env.REACT_APP_GA4_MEASUREMENT_ID || process.env.REACT_APP_GA_MEASUREMENT_ID || '').trim();
const getPosthogKey = () => String(process.env.REACT_APP_POSTHOG_KEY || '').trim();
const getPosthogHost = () => String(process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com').trim();
const requiresConsent = () => toBool(process.env.REACT_APP_ANALYTICS_REQUIRE_CONSENT);

const safeParse = (value) => {
	try {
		return JSON.parse(value);
	} catch (err) {
		return null;
	}
};

const clearStoredAttribution = () => {
	if (!canUseBrowser()) return;
	window.localStorage.removeItem(ATTRIBUTION_KEY);
};

const getStoredAttribution = () => {
	if (!canUseBrowser()) return {};
	const raw = window.localStorage.getItem(ATTRIBUTION_KEY);
	return safeParse(raw) || {};
};

const setStoredAttribution = (data) => {
	if (!canUseBrowser()) return;
	window.localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(data));
};

export const getAnalyticsConsent = () => {
	if (!canUseBrowser()) return 'unknown';
	const stored = window.localStorage.getItem(CONSENT_KEY);
	if (stored === 'granted' || stored === 'denied') return stored;
	return 'unknown';
};

export const setAnalyticsConsent = (status) => {
	if (!canUseBrowser()) return;
	if (status !== 'granted' && status !== 'denied') return;
	window.localStorage.setItem(CONSENT_KEY, status);
	if (status === 'granted') {
		initializeAnalytics();
		return;
	}
	clearStoredAttribution();
};

const hasTrackingConsent = () => {
	if (!requiresConsent()) return true;
	return getAnalyticsConsent() === 'granted';
};

const ensureDataLayer = () => {
	if (!canUseBrowser()) return;
	if (!Array.isArray(window.dataLayer)) {
		window.dataLayer = [];
	}
};

const ensureGtagFunction = () => {
	if (!canUseBrowser()) return;
	if (typeof window.gtag !== 'function') {
		window.gtag = function gtag() {
			window.dataLayer.push(arguments);
		};
	}
};

const initializePosthog = () => {
	if (!canUseBrowser() || posthogBootstrapped) return;
	const key = getPosthogKey();
	if (!key || !hasTrackingConsent()) return;
	posthog.init(key, {
		api_host: getPosthogHost(),
		// We send pageviews manually via trackPageView so screens that
		// matter — landing, pricing, demo, register — are explicit rather
		// than counting all React-router transitions.
		capture_pageview: false,
		capture_pageleave: true,
		persistence: 'localStorage+cookie',
		loaded: (ph) => {
			if (process.env.NODE_ENV !== 'production') {
				ph.opt_in_capturing();
			}
		},
	});
	posthogBootstrapped = true;
};

export const initializeAnalytics = () => {
	if (!canUseBrowser()) return false;
	if (!hasTrackingConsent()) return false;

	const measurementId = getMeasurementId();
	const posthogKey = getPosthogKey();

	// GA4 sink
	if (measurementId) {
		ensureDataLayer();
		ensureGtagFunction();
		if (!gaBootstrapped) {
			window.gtag('js', new Date());
			window.gtag('config', measurementId, {
				send_page_view: false,
				anonymize_ip: true,
			});
			gaBootstrapped = true;
		}
		const scriptSrc = `${GA4_SCRIPT_BASE}${encodeURIComponent(measurementId)}`;
		const existingScript = document.querySelector(`script[${GA_SCRIPT_ATTR}]`);
		if (!existingScript) {
			const script = document.createElement('script');
			script.async = true;
			script.src = scriptSrc;
			script.setAttribute(GA_SCRIPT_ATTR, measurementId);
			document.head.appendChild(script);
		}
	}

	// PostHog sink (preferred — funnels, replay, flags)
	if (posthogKey) initializePosthog();

	return Boolean(measurementId || posthogKey);
};

// Identify a known user on PostHog after login / register success.
// Call with the user's id; properties are optional.
export const identifyUser = (userId, properties = {}) => {
	if (!canUseBrowser() || !userId || !hasTrackingConsent()) return;
	initializeAnalytics();
	if (posthogBootstrapped && typeof posthog.identify === 'function') {
		posthog.identify(String(userId), properties);
	}
};

export const resetIdentity = () => {
	if (!canUseBrowser()) return;
	if (posthogBootstrapped && typeof posthog.reset === 'function') {
		posthog.reset();
	}
};

export const captureAttributionFromLocation = (search = '') => {
	if (!canUseBrowser()) return {};
	if (!hasTrackingConsent()) return {};
	const params = new URLSearchParams(search || window.location.search);
	const existing = getStoredAttribution();
	const next = { ...existing };

	for (const key of ATTR_KEYS) {
		const value = params.get(key);
		if (value) next[key] = value;
	}

	if (!next.first_seen_at) {
		next.first_seen_at = new Date().toISOString();
	}
	next.last_seen_at = new Date().toISOString();

	setStoredAttribution(next);
	return next;
};

export const getAttribution = () => {
	if (!hasTrackingConsent()) return {};
	return getStoredAttribution();
};

export const trackEvent = (eventName, properties = {}) => {
	if (!canUseBrowser() || !eventName) return;
	initializeAnalytics();

	const payload = {
		event: eventName,
		page_path: window.location.pathname,
		page_search: window.location.search,
		timestamp: new Date().toISOString(),
		...properties,
	};

	const attribution = getAttribution();
	if (Object.keys(attribution).length > 0) {
		payload.attribution = attribution;
	}

	if (Array.isArray(window.dataLayer) && hasTrackingConsent()) {
		window.dataLayer.push(payload);
	}

	if (typeof window.gtag === 'function' && hasTrackingConsent()) {
		window.gtag('event', eventName, properties);
	}

	if (posthogBootstrapped && hasTrackingConsent() && typeof posthog.capture === 'function') {
		posthog.capture(eventName, {
			$current_url: window.location.href,
			page_path: window.location.pathname,
			...properties,
			...(Object.keys(getAttribution()).length > 0 ? { attribution: getAttribution() } : {}),
		});
	}

	if (process.env.NODE_ENV !== 'production') {
		// Useful while no external analytics vendor is configured.
		console.info('[analytics]', payload);
	}
};

export const trackPageView = (pageName, properties = {}) => {
	trackEvent('page_view', {
		page_name: pageName,
		...properties,
	});
};
